/**
 * Dữ liệu của màn `/banned` đi qua `history.state`, KHÔNG qua URL (booknow/frontend#1181).
 *
 * Trước đây email nằm ở `?email=` nên ai cũng dựng được link `/banned?email=<bất kỳ>` trên đúng domain
 * thật, và địa chỉ đó rơi vào access log / lịch sử trình duyệt. `history.state` chỉ code cùng origin
 * ghi được, sống qua F5 và Back/Forward của CHÍNH entry đó, và mất khi mở ở tab mới — đúng thứ màn
 * này cần: nó là kết quả của một lần đăng nhập vừa bị từ chối, không phải một trang để chia sẻ.
 *
 * Khoá `banned` có mặt là "vừa bị khoá"; `email` có thể `null` (nhánh Google không có nguồn email nào
 * đáng tin). Ghi đè tường minh thay vì bỏ khoá: `replace` của vue-router GỘP state cũ vào.
 */
import type { Router } from 'vue-router';

const STATE_KEY = 'banned';

/** Tên route của màn — khai ở đây (không ở `route.ts`) để `route.ts` import file này mà không vòng. */
export const BANNED_ROUTE_NAME = 'banned';

export interface BannedState {
  email: string | null;
}

/** State màn đăng nhập ghi khi BE trả `ACCOUNT_BANNED`. */
export const toBannedState = (email: string | null) => ({ [STATE_KEY]: { email } });

/** State xoá dấu banned — dùng khi rời `/banned`, để entry sau không mang theo email cũ. */
export const CLEARED_BANNED_STATE = { [STATE_KEY]: null };

/** Dấu banned của entry hiện tại, hoặc `null` nếu entry không do màn đăng nhập đưa tới. */
export const readBannedState = (): BannedState | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw: unknown = window.history.state?.[STATE_KEY];
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const email = (raw as { email?: unknown }).email;
  return { email: typeof email === 'string' && email ? email : null };
};

/**
 * Dấu banned CỦA CHÍNH entry `fullPath` — cho guard. Lúc push/replace trong app, `history.state` còn
 * là của entry đang đứng, và `replace` của vue-router GỘP state cũ: một entry rời `/banned` bằng
 * `replace` mà không xoá dấu thì mang nó theo. Chỉ tin dấu khi `current` (vue-router ghi vào state của
 * mọi entry — `useHistory.ts` cũng đọc trường anh em `back`) đúng là đích; tải lại / Back-Forward thì
 * state đã là của đích.
 */
export const readBannedStateOf = (fullPath: string): BannedState | null =>
  typeof window !== 'undefined' && window.history.state?.current === fullPath ? readBannedState() : null;

/**
 * Cờ một-lần "màn đăng nhập vừa khởi phát điều hướng sang `/banned`".
 *
 * Cần vì lúc `beforeEnter` chạy, vue-router CHƯA ghi state mới — `history.state` vẫn là của entry
 * `/signin` — nên guard không thấy dấu banned của chính lần điều hướng này. Không thay được bằng
 * `from.name === 'signin'`: gõ hash `#/banned` (Electron) hay Forward từ `/signin` cũng có `from` đó
 * mà không có lần đăng nhập nào vừa bị từ chối. Sống trong bộ nhớ module ⇒ tải lại là mất, đúng ý:
 * sau tải lại thì guard đọc dấu banned từ `history.state`.
 *
 * Giữ id của lần khởi phát gần nhất (0 = không có) để lần cũ bị thay thế không dọn mất cờ của lần mới.
 */
let pendingEntry = 0;
let lastEntry = 0;

/** Bật cờ + trả đích `/banned`. Chỉ `goToBanned` và test gọi — đi thẳng là mất phần dọn cờ. */
export const startBannedEntry = (email: string | null) => {
  lastEntry += 1;
  pendingEntry = lastEntry;
  return { name: BANNED_ROUTE_NAME, state: toBannedState(email) };
};

/** Guard đọc rồi xoá cờ — mỗi lần khởi phát cho qua đúng một lần điều hướng. */
export const consumeBannedEntry = (): boolean => {
  const started = pendingEntry !== 0;
  pendingEntry = 0;
  return started;
};

/**
 * Điều hướng sang `/banned` — CHỈ màn đăng nhập gọi, ngay khi BE trả `ACCOUNT_BANNED`.
 *
 * Cờ sống đúng trong lần điều hướng này: settle (xong, bị huỷ, bị điều hướng khác thay) là dọn —
 * không thì một lần bị huỷ TRƯỚC khi tới guard `/banned` để cờ sót, và lần vào `/banned` sau được cho
 * qua không lý do. Chỉ dọn khi cờ vẫn là của CHÍNH lần này: hai lần chồng nhau thì lần cũ settle sau
 * khi lần mới đã bật cờ.
 */
export const goToBanned = (router: Pick<Router, 'replace'>, email: string | null) => {
  const location = startBannedEntry(email);
  const entry = pendingEntry;
  return router.replace(location).finally(() => {
    if (pendingEntry === entry) {
      pendingEntry = 0;
    }
  });
};
